import React, { useEffect, useId, useRef } from "react";
import { IconName, RootState, Screen, ToastMessage } from "../types/domain";
import { classNames, initials } from "../utils/auth";
import { Icon } from "./Icon";
import academyLogo from "../../../shared/brand/chatoner-ai-academy-logo-landscape.webp?no-inline";

export const ACADEMY_LOGO_SRC = academyLogo;
export function StatusChip({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "green" | "orange" | "blue" | "red";
}) {
  return <span className={classNames("status-chip", tone !== "default" && tone)}>{children}</span>;
}

export function Button({
  children,
  tone = "ghost",
  size = "normal",
  type = "button",
  disabled,
  onClick,
  className = "",
}: {
  children: React.ReactNode;
  tone?: "primary" | "orange" | "ghost" | "danger" | "link";
  size?: "normal" | "small";
  type?: "button" | "submit";
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <button
      type={type}
      className={classNames("btn", tone, size === "small" && "small", className)}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

export function PageHead({
  icon,
  eyebrow,
  title,
  description,
  action,
}: {
  icon?: IconName;
  eyebrow?: string;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="page-head">
      {icon ? (
        <span className="page-head-icon">
          <Icon name={icon} size={22} />
        </span>
      ) : null}
      <div className="page-head-copy">
        {eyebrow ? <div className="eyebrow">{eyebrow}</div> : null}
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      {action}
    </div>
  );
}

export function Callout({
  icon = "info",
  title,
  children,
  tone = "default",
}: {
  icon?: IconName;
  title: string;
  children: React.ReactNode;
  tone?: "default" | "orange" | "blue" | "red";
}) {
  return (
    <div className={classNames("callout", tone !== "default" && tone)}>
      <span>
        <Icon name={icon} size={16} />
      </span>
      <div>
        <strong>{title}</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  required,
  icon,
  hint,
  error,
  autoComplete,
  disabled,
  endAction,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  icon?: IconName;
  hint?: string;
  error?: string;
  autoComplete?: string;
  disabled?: boolean;
  endAction?: React.ReactNode;
  className?: string;
}) {
  const inputId = useId();
  const messageId = `${inputId}-message`;
  return (
    <div className={classNames("form-field", className)}>
      <label htmlFor={inputId}>{label}</label>
      <div className={classNames("input-wrap", icon && "has-icon")}>
        {icon ? <Icon name={icon} size={16} /> : null}
        <input
          id={inputId}
          className={error ? "error" : ""}
          type={type}
          value={value}
          onChange={event => onChange(event.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          disabled={disabled}
          required={required}
          aria-invalid={Boolean(error)}
          aria-describedby={error || hint ? messageId : undefined}
        />
        {endAction}
      </div>
      {error ? (
        <span id={messageId} className="field-error">
          <Icon name="alert" size={12} />
          {error}
        </span>
      ) : hint ? (
        <span id={messageId} className="field-hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  required,
  hint,
  error,
  icon,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<string | { value: string; label: string }>;
  required?: boolean;
  hint?: string;
  error?: string;
  icon?: IconName;
  disabled?: boolean;
}) {
  const inputId = useId();
  const messageId = `${inputId}-message`;
  return (
    <div className="form-field">
      <label htmlFor={inputId}>{label}</label>
      <div className={classNames("input-wrap", icon && "has-icon")}>
        {icon ? <Icon name={icon} size={16} /> : null}
        <select
          id={inputId}
          className={error ? "error" : ""}
          value={value}
          onChange={event => onChange(event.target.value)}
          disabled={disabled}
          required={required}
          aria-invalid={Boolean(error)}
          aria-describedby={error || hint ? messageId : undefined}
        >
          {options.map(option =>
            typeof option === "string" ? (
              <option key={option} value={option}>
                {option}
              </option>
            ) : (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ),
          )}
        </select>
      </div>
      {error ? (
        <span id={messageId} className="field-error">
          <Icon name="alert" size={12} />
          {error}
        </span>
      ) : hint ? (
        <span id={messageId} className="field-hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function TextAreaField({
  label,
  value,
  onChange,
  placeholder,
  required,
  hint,
  error,
  rows = 4,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  rows?: number;
}) {
  const inputId = useId();
  const messageId = `${inputId}-message`;
  return (
    <div className="form-field">
      <label htmlFor={inputId}>{label}</label>
      <textarea
        id={inputId}
        className={error ? "error" : ""}
        rows={rows}
        value={value}
        onChange={event => onChange(event.target.value)}
        placeholder={placeholder}
        required={required}
        aria-invalid={Boolean(error)}
        aria-describedby={error || hint ? messageId : undefined}
      />
      {error ? (
        <span id={messageId} className="field-error">
          <Icon name="alert" size={12} />
          {error}
        </span>
      ) : hint ? (
        <span id={messageId} className="field-hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function CheckCard({
  selected,
  onClick,
  title,
  detail,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  detail?: string;
}) {
  return (
    <button type="button" className={classNames("check-card", selected && "selected")} onClick={onClick}>
      <span className="check-box">
        <Icon name="check" size={13} />
      </span>
      <span>
        <strong>{title}</strong>
        {detail ? <span>{detail}</span> : null}
      </span>
    </button>
  );
}

export function ToggleRow({
  label,
  detail,
  value,
  onChange,
  required,
  optional,
}: {
  label: string;
  detail: string;
  value: boolean;
  onChange: (value: boolean) => void;
  required?: boolean;
  optional?: boolean;
}) {
  const requirementLabel = required ? ", required" : optional ? ", optional" : "";
  return (
    <button
      type="button"
      className={classNames("check-card", value && "selected")}
      onClick={() => onChange(!value)}
      aria-pressed={value}
      aria-label={`${label}${requirementLabel}`}
    >
      <span className="check-box">
        <Icon name="check" size={13} />
      </span>
      <span>
        <strong>
          {label}
          {optional ? <span className="optional-marker"> optional</span> : null}
        </strong>
        <span>{detail}</span>
      </span>
    </button>
  );
}

export function BrandPanel() {
  return (
    <aside className="brand-panel" aria-label="Academy access overview" tabIndex={0}>
      <div className="brand-head">
        <img
          className="brand-lockup"
          src={ACADEMY_LOGO_SRC}
          alt="Chatoner AI Academy"
          width="960"
          height="225"
          decoding="async"
        />
        <span className="brand-context">Access · Applications · Onboarding</span>
      </div>
      <div className="brand-copy">
        <span className="brand-kicker">
          <Icon name="shield" size={13} />
          Human-reviewed entry
        </span>
        <h1>One secure start for every Academy role.</h1>
        <p>
          Apply to learn, support a child, teach, proctor, sponsor a team or configure an institution—then
          enter only the workspace and authority your verified role allows.
        </p>
      </div>
      <div className="brand-visual" aria-hidden="true">
        <div className="visual-track" />
        <span className="visual-node one">
          <Icon name="user" size={21} />
        </span>
        <span className="visual-node two">
          <Icon name="clipboard" size={21} />
        </span>
        <span className="visual-node three">
          <Icon name="graduation" size={21} />
        </span>
        <span className="visual-label one">Identity</span>
        <span className="visual-label two">Application</span>
        <span className="visual-label three">Workspace</span>
      </div>
      <div className="brand-features">
        <div className="brand-feature">
          <span>
            <Icon name="calendar" size={16} />
          </span>
          <div>
            <strong>Cohort-specific admission</strong>
            <small>Every applicant chooses the exact intake, schedule, language and status.</small>
          </div>
        </div>
        <div className="brand-feature">
          <span>
            <Icon name="child" size={16} />
          </span>
          <div>
            <strong>Age-aware family controls</strong>
            <small>Parent-primary, guardian-required, youth and adult paths remain distinct.</small>
          </div>
        </div>
        <div className="brand-feature">
          <span>
            <Icon name="lock" size={16} />
          </span>
          <div>
            <strong>Least-privilege role entry</strong>
            <small>Privileged workspaces require verified assignment, MFA and scoped authority.</small>
          </div>
        </div>
      </div>
      <div className="brand-foot">
        <Icon name="globe" size={13} />
        academy.chatoner.com/auth · Chatoner Class · Chatoner AI Labs
      </div>
    </aside>
  );
}

export function TopBar({
  state,
  setScreen,
  reset,
  openHelp,
}: {
  state: RootState;
  setScreen: (screen: Screen) => void;
  reset: () => void;
  openHelp: () => void;
}) {
  const authScreens: Screen[] = [
    "welcome",
    "signin",
    "signup",
    "verify-email",
    "mfa",
    "forgot-password",
    "reset-password",
    "invite",
    "workspaces",
  ];
  const applicationScreens: Screen[] = ["application", "status", "offer", "checkout"];
  const stage = authScreens.includes(state.screen)
    ? "auth"
    : applicationScreens.includes(state.screen)
      ? "application"
      : "onboarding";
  const authDone = state.authenticated || state.application.status !== "draft";
  const applicationDone = ["accepted", "enrolled"].includes(state.application.status);
  const openApplicationStage = () => {
    const status = state.application.status;
    setScreen(status === "draft" ? "application" : status === "offered" ? "offer" : "status");
  };
  return (
    <header className="topbar">
      <div className="mobile-brand">
        <img src={ACADEMY_LOGO_SRC} alt="Chatoner AI Academy" width="960" height="225" decoding="async" />
      </div>
      <nav className="journey-tabs" aria-label="Entry journey">
        <button
          type="button"
          className={classNames(
            "journey-tab",
            stage === "auth" && "active",
            authDone && stage !== "auth" && "done",
          )}
          onClick={() => setScreen(state.authenticated ? "workspaces" : "welcome")}
        >
          <span className="journey-number">{authDone ? <Icon name="check" size={12} /> : "1"}</span>
          <span>Access</span>
        </button>
        <button
          type="button"
          className={classNames(
            "journey-tab",
            stage === "application" && "active",
            applicationDone && stage === "onboarding" && "done",
          )}
          onClick={openApplicationStage}
        >
          <span className="journey-number">{applicationDone ? <Icon name="check" size={12} /> : "2"}</span>
          <span>Application</span>
        </button>
        <button
          type="button"
          className={classNames("journey-tab", stage === "onboarding" && "active")}
          disabled={!applicationDone}
          title={
            applicationDone
              ? "Open onboarding"
              : "Onboarding opens after an accepted offer or verified invitation"
          }
          onClick={() => setScreen("onboarding")}
        >
          <span className="journey-number">3</span>
          <span>Onboarding</span>
        </button>
      </nav>
      <div className="topbar-spacer" />
      <button
        type="button"
        className="topbar-action topbar-utility"
        onClick={openHelp}
        aria-label="Help and role guide"
      >
        <Icon name="info" size={14} />
        <span>Help & role guide</span>
      </button>
      <button type="button" className="topbar-action topbar-utility" onClick={reset} aria-label="Reset demo">
        <Icon name="refresh" size={14} />
        <span>Reset demo</span>
      </button>
      {state.authName ? (
        <button
          type="button"
          className="topbar-avatar"
          aria-label="Open account workspaces"
          onClick={() => setScreen("workspaces")}
        >
          {initials(state.authName.split(" ")[0] || "C", state.authName.split(" ")[1] || "A")}
        </button>
      ) : null}
    </header>
  );
}

export function Modal({
  title,
  subtitle,
  icon,
  children,
  actions,
  onClose,
  className = "",
}: {
  title: string;
  subtitle: string;
  icon: IconName;
  children: React.ReactNode;
  actions?: React.ReactNode;
  onClose: () => void;
  className?: string;
}) {
  const titleId = useId();
  const subtitleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    const frame = window.requestAnimationFrame(() => {
      const preferredControl = dialogRef.current?.querySelector<HTMLElement>("[data-modal-autofocus]");
      (preferredControl || dialogRef.current)?.focus();
    });
    document.body.style.overflow = "hidden";

    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus({ preventScroll: true });
    };
  }, [title]);

  function handleDialogKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key !== "Tab") return;

    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), details > summary, [tabindex]:not([tabindex="-1"])',
      ) || [],
    ).filter(element => !element.hidden && element.getClientRects().length > 0);
    if (!focusable.length) {
      event.preventDefault();
      dialogRef.current?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={classNames("modal", className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitleId}
        tabIndex={-1}
        onKeyDown={handleDialogKeyDown}
      >
        <div className="modal-head">
          <span>
            <Icon name={icon} size={19} />
          </span>
          <div>
            <strong id={titleId}>{title}</strong>
            <small id={subtitleId}>{subtitle}</small>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {actions ? <div className="modal-actions">{actions}</div> : null}
      </div>
    </div>
  );
}

export function ToastStack({
  messages,
  dismiss,
}: {
  messages: ToastMessage[];
  dismiss: (id: number) => void;
}) {
  return (
    <div className="toast-stack" aria-live="polite">
      {messages.map(message => (
        <div className={classNames("toast", message.tone)} key={message.id}>
          <span>
            <Icon
              name={
                message.tone === "success"
                  ? "check"
                  : message.tone === "warning"
                    ? "alert"
                    : message.tone === "danger"
                      ? "x"
                      : "info"
              }
              size={16}
            />
          </span>
          <div>
            <strong>{message.title}</strong>
            <p>{message.detail}</p>
          </div>
          <button type="button" onClick={() => dismiss(message.id)} aria-label="Dismiss">
            <Icon name="x" size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
